import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePartnerProfile } from "@/hooks/use-partner-profile";
import { usePartnerResources } from "@/hooks/use-partner-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { 
  Filter,
  Download,
  FileImage,
  FileVideo,
  FileSpreadsheet,
  FileText,
  File
} from "lucide-react";
import { format } from "date-fns";

const PartnerResources = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { session } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Get partner profile using the enhanced hook
  const { data: partnerProfile, isLoading: profileLoading } = usePartnerProfile(session?.user?.id);
  const { data: resources = [] } = usePartnerResources(partnerProfile?.id);

  if (isLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!hasCrmAccess || !partnerProfile) {
    return <Navigate to="/dashboard" replace />;
  }

  // File type helpers
  const getFileIcon = (fileType?: string) => {
    if (!fileType) return File;
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) return FileText;
    if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return FileImage;
    if (type.includes('video') || type.includes('mp4') || type.includes('mov')) return FileVideo;
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('csv')) return FileSpreadsheet;
    if (type.includes('word') || type.includes('doc')) return FileText;
    if (type.includes('powerpoint') || type.includes('ppt')) return FileText;
    
    return File;
  };

  const getFileTypeColor = (fileType?: string) => {
    if (!fileType) return 'text-slate-500';
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) return 'text-red-500';
    if (type.includes('image')) return 'text-green-500';
    if (type.includes('video')) return 'text-purple-500';
    if (type.includes('excel') || type.includes('csv')) return 'text-emerald-500';
    if (type.includes('word') || type.includes('doc')) return 'text-blue-500';
    if (type.includes('powerpoint') || type.includes('ppt')) return 'text-orange-500';
    
    return 'text-slate-500';
  };

  const handleDownload = (url: string, title: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = title;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Resource filtering
  const categories = ["all", ...new Set(resources.map(r => r.category || "general"))];
  const filteredResources = selectedCategory === "all" 
    ? resources 
    : resources.filter(r => (r.category || "general") === selectedCategory);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.partnerResources} />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
            
            <div className="space-y-6">
              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <div className="flex space-x-2">
                  {categories.map((category) => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(category)}
                      className={selectedCategory === category ? "bg-[#DFEF97] text-black hover:bg-[#d1e587]" : ""}
                    >
                      {category === "all" ? "All" : category.charAt(0).toUpperCase() + category.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Resources Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredResources.map((resource) => {
                  const FileIcon = getFileIcon(resource.file_type);
                  const iconColor = getFileTypeColor(resource.file_type);
                  
                  return (
                    <Card key={resource.id} className="group hover:shadow-md transition-all duration-200 border-slate-200 bg-white hover:border-slate-300">
                      <CardHeader className="pb-2 space-y-2">
                        <div className="flex items-start justify-between">
                          <div className={`p-1.5 rounded-md bg-slate-50 group-hover:bg-slate-100 transition-colors ${iconColor}`}>
                            <FileIcon className="h-4 w-4" />
                          </div>
                          <Badge 
                            variant="secondary" 
                            className="text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200"
                          >
                            {resource.category || "general"}
                          </Badge>
                        </div>
                        
                        <div>
                          <CardTitle className="text-sm font-semibold text-slate-800 line-clamp-2 leading-tight">
                            {resource.title}
                          </CardTitle>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-0 space-y-3">
                        {/* File Details */}
                        <div className="text-xs text-slate-500">
                          <div className="font-medium">
                            {resource.file_type?.split('/')[1]?.toUpperCase() || "FILE"} • {resource.file_size ? `${Math.round(resource.file_size / 1024)} KB` : "—"}
                          </div>
                          <div className="mt-1">
                            {resource.created_at ? format(new Date(resource.created_at), 'MMM d, yyyy') : "Unknown date"}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(resource.file_url, resource.title)}
                            className="flex-1 hover:bg-slate-50 border-slate-200"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* No resources message */}
              {filteredResources.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>No resources found in this category</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default PartnerResources;